import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Select, Tag } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment-timezone';
import { getAllTransactionsAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';

const { RangePicker } = DatePicker;
const { Option } = Select;

export const BasicTable: React.FC = () => {
  const [filters, setFilters] = useState({
    createdAtRange: [] as any[],
    name: '',
    phone: '',
    type: '',
    amount: '',
  });
  const [sorterState, setSorterState] = useState<{ sortBy: string; sortOrder: string }>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const dispatch = useAppDispatch();

  // Fetch data with updated filters, sorting, and pagination
  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params: any = {
      current: pagination.current,
      pageSize: pagination.pageSize,
      sortBy: sorterState.sortBy,
      sortOrder: sorterState.sortOrder,
    };

    if (filters.name) params.name = filters.name;
    if (filters.phone) params.phone = filters.phone;
    if (filters.type) params.type = filters.type;
    if (filters.amount) params.amount = filters.amount;

    if (filters.createdAtRange && filters.createdAtRange.length === 2) {
      params.createdAtRange = [
        moment.tz(moment(filters.createdAtRange[0]).format('YYYY-MM-DD'), 'Asia/Kolkata').startOf('day').toISOString(),
        moment.tz(moment(filters.createdAtRange[1]).format('YYYY-MM-DD'), 'Asia/Kolkata').endOf('day').toISOString(),
      ].join(',');
    }

    dispatch(getAllTransactionsAction(params))
      .unwrap()
      .then((response) => {
        setTableData({
          data: response.data || [],
          loading: false,
          total: response.total || 0,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch transactions' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, filters, sorterState, dispatch]);

  useEffect(() => {
    function hideError(e: any) {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) resizeObserverErr.setAttribute('style', 'display: none');
        if (resizeObserverErrDiv) resizeObserverErrDiv.setAttribute('style', 'display: none');
      }
    }

    window.addEventListener('error', hideError);
    return () => {
      window.removeEventListener('error', hideError);
    };
  }, []);

  // Trigger fetch when filters, pagination, or sorting change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Handle table pagination & sorter changes
  const handleTableChange = (paginationInfo: any, tableFilters: any, sorter: any) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
    if (sorter && sorter.field) {
      setSorterState({
        sortBy: sorter.field,
        sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc',
      });
    }
  };

  const handleClearFilters = () => {
    setFilters({
      createdAtRange: [],
      name: '',
      phone: '',
      type: '',
      amount: '',
    });
    setSorterState({ sortBy: 'createdAt', sortOrder: 'desc' });
    setTimeout(() => {
      setPagination({ current: 1, pageSize: 20 });
    }, 100);
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Customer Name',
      dataIndex: 'name',
      render: (text, record) => (
        <span>
          {record?.name ||
            record?.addressDetails?.customer_name ||
            record?.address?.customer_name ||
            record?.clientDetails?.address?.customer_name ||
            record?.clientDetails?.name ||
            'Not available'}
        </span>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Name"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('name', value);
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Mobile Number',
      dataIndex: 'phone',
      render: (text, record) => (
        <span>
          {record?.phone ||
            record?.clientDetails?.phone ||
            record?.addressDetails?.phone ||
            record?.address?.phone ||
            'Not available'}
        </span>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Mobile Number"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('phone', value);
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Transaction Date',
      dataIndex: 'createdAt',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      defaultSortOrder: 'descend',
      render: (text, record) => (
        <span>
          {record.createdAt
            ? moment(record.createdAt).tz('Asia/Kolkata').format('DD-MMM-YYYY hh:mm A')
            : 'Not available'}
        </span>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            value={
              selectedKeys.length === 2
                ? (selectedKeys as any)
                : filters.createdAtRange && filters.createdAtRange.length === 2
                ? [moment(filters.createdAtRange[0]), moment(filters.createdAtRange[1])] as any
                : null
            }
            onChange={(dates) => {
              setSelectedKeys(dates || []);
              handleFilterChange('createdAtRange', dates || []);
            }}
            style={{ width: 230, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Transaction Type',
      dataIndex: 'type',
      render: (text, record) => (
        <Tag color={record.isCredit ? 'green' : 'volcano'}>
          {text === 'Capture' ? 'Cancelled By User' : text || 'N/A'} {record.isCredit ? '(CR)' : '(DR)'}
        </Tag>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            value={selectedKeys[0] || ''}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('type', value);
            }}
            style={{ width: 188, marginBottom: 8 }}
            placeholder="Select Status"
          >
            <Option value="Wallet">Wallet</Option>
            <Option value="Razorpay">Razorpay</Option>
            <Option value="Capture">Cancelled By User</Option>
          </Select>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (text, record) => (
        <span style={{ fontWeight: 600, color: record.isCredit ? '#52c41a' : '#f5222d' }}>
          {record.isCredit ? '+ ' : '- '}₹{record.amount ?? 0}
        </span>
      ),
    },
    {
      title: 'Payment Status',
      dataIndex: 'status',
      render: (text, record) => {
        const status = record.status || 'Completed';
        const color =
          status === 'Completed'
            ? 'green'
            : status === 'Failed' || status === 'Cancelled'
            ? 'red'
            : status === 'Pending'
            ? 'orange'
            : 'blue';
        return <Tag color={color}>{status}</Tag>;
      },
    },
  ];

  return (
    <>
      <Button style={{ marginBottom: '20px' }} onClick={handleClearFilters}>
        Clear Filters
      </Button>
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
      />
    </>
  );
};
